import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EditClientDialog } from "./EditClientDialog";
import { Button } from "@/components/ui/button";
import { Trash2, History, Lock, Search, Edit2, Link2, Key } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { PublicSubscriptionLink } from "@/components/subscriptions/PublicSubscriptionLink";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
interface Client {
  id: string;
  school_id: string;
  nama_sekolah: string;
  hasSubscriptions?: boolean;
  hasAccessToken?: boolean;
  accessToken?: string;
}
interface ClientTableProps {
  clients: Client[];
  loading: boolean;
  onDelete: (id: string) => void;
  onClientUpdated: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}
export const ClientTable = ({
  clients,
  loading,
  onDelete,
  onClientUpdated,
  searchQuery,
  onSearchChange,
  page,
  pageSize,
  totalCount,
  onPageChange
}: ClientTableProps) => {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [clientsWithStatus, setClientsWithStatus] = useState<Client[]>(clients);
  useEffect(() => {
    const checkStatus = async () => {
      const updatedClients = await Promise.all(clients.map(async client => {
        const {
          data: subscriptions,
          error: subscriptionError
        } = await supabase.from('subscriptions').select('id').eq('client_id', client.id).limit(1);
        const {
          data: accessTokens,
          error: tokenError
        } = await supabase.from('school_access_tokens').select('token').eq('school_id', client.school_id).gt('expires_at', new Date().toISOString()).order('created_at', {
          ascending: false
        }).limit(1);
        if (subscriptionError || tokenError) {
          console.error('Error checking status:', subscriptionError || tokenError);
          return {
            ...client,
            hasSubscriptions: false,
            hasAccessToken: false
          };
        }
        return {
          ...client,
          hasSubscriptions: subscriptions && subscriptions.length > 0,
          hasAccessToken: accessTokens && accessTokens.length > 0,
          accessToken: accessTokens && accessTokens.length > 0 ? accessTokens[0].token : undefined
        };
      }));
      setClientsWithStatus(updatedClients);
    };
    checkStatus();
  }, [clients]);
  const handleGenerateToken = async (schoolId: string) => {
    try {
      const token = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to generate tokens",
          variant: "destructive"
        });
        return;
      }
      const {
        error
      } = await supabase.from('school_access_tokens').insert({
        school_id: schoolId,
        token: token,
        created_by: user.id
      });
      if (error) {
        console.error('Error generating token:', error);
        toast({
          title: "Error",
          description: "Failed to generate access token",
          variant: "destructive"
        });
        return;
      }
      setClientsWithStatus(prev => prev.map(client => client.school_id === schoolId ? {
        ...client,
        hasAccessToken: true,
        accessToken: token
      } : client));
      toast({
        title: "Success",
        description: "Access token generated successfully"
      });
    } catch (error) {
      console.error('Error in handleGenerateToken:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };
  const copyLinkWithToken = (schoolId: string, token: string) => {
    const url = `${window.location.origin}/subscriptions/public?schoolId=${encodeURIComponent(schoolId)}&token=${token}`;
    navigator.clipboard.writeText(url);
    toast({
      title: "Link Copied",
      description: "Secure subscription link copied to clipboard"
    });
  };
  const totalPages = Math.ceil(totalCount / pageSize);
  return <div className="space-y-4">
      <div className="flex items-center justify-end">
        <div className="relative w-72">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input placeholder="Search schools..." value={searchQuery} onChange={e => onSearchChange(e.target.value)} className="pl-9" />
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>School ID</TableHead>
            <TableHead>Nama Sekolah</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? <TableRow>
              <TableCell colSpan={3} className="text-center py-4">
                Loading...
              </TableCell>
            </TableRow> : clientsWithStatus.length === 0 ? <TableRow>
              <TableCell colSpan={3} className="text-center py-4">
                {searchQuery ? "No matching schools found" : "No schools found"}
              </TableCell>
            </TableRow> : clientsWithStatus.map(client => <TableRow key={client.id} className="h-14">
                <TableCell className="py-2">{client.school_id}</TableCell>
                <TableCell className="py-4 flex items-center space-x-2">
                  {client.nama_sekolah}
                  {client.hasSubscriptions && <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 text-blue-800 ml-2">
                            <Lock className="h-3 w-3" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>School has active or past subscriptions</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>}
                </TableCell>
                <TableCell className="py-2">
                  <div className="flex items-center gap-2">
                    <EditClientDialog clientId={client.id} initialValues={client} onClientUpdated={onClientUpdated} />
                    {client.hasSubscriptions ? <Button variant="ghost" size="icon" disabled title="Cannot delete school with active or past subscriptions">
                        <Lock className="h-4 w-4 text-gray-400" />
                      </Button> : <Button variant="ghost" size="icon" onClick={() => onDelete(client.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>}
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/transaction-history/${client.school_id}`)}>
                      <History className="h-4 w-4 text-blue-500" />
                    </Button>
                    {client.hasAccessToken && client.accessToken ? <Button variant="ghost" size="icon" title="Copy secure access link" onClick={() => copyLinkWithToken(client.school_id, client.accessToken!)}>
                        <Link2 className="h-4 w-4 text-green-500" />
                      </Button> : <Button variant="ghost" size="icon" title="Generate access token" onClick={() => handleGenerateToken(client.school_id)}>
                        <Key className="h-4 w-4 text-gray-500" />
                      </Button>}
                  </div>
                </TableCell>
              </TableRow>)}
        </TableBody>
      </Table>

      {totalPages > 1 && <div className="flex items-center justify-end space-x-2 py-4">
          <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page === 1 || loading}>
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page === totalPages || loading}>
            Next
          </Button>
        </div>}
    </div>;
};